import React from 'react';
import {
  ResponsiveContainer,
} from 'recharts';
import { BaseBarChart } from '../Common/BarChart';
import { JsonPropsType } from '../../../component-loader';
import { GenericTableRawData } from './types';
import { formatData } from '../../../utils/deck';

type ChartData = {
  name: string;
  amount: number
};

const SHARED_MARGINS = {
  top: 25,
  right: 0,
  left: -100,
  bottom: 0,
};

// Raw keys comes with spaces, but they are deleted in the formatted data
interface PortfolioMetricsMultipleBarChartsExpectedColumns {
  '%UF': number;
  '%CLP': number;
  'Duración': number;
}

interface PortfolioMetricsMultipleBarChartsFormattedData {
  PORTF : PortfolioMetricsMultipleBarChartsExpectedColumns;
  BENCH: PortfolioMetricsMultipleBarChartsExpectedColumns;
  ACT: PortfolioMetricsMultipleBarChartsExpectedColumns;
}

export function PortfolioMetricsMultipleBarCharts(props: JsonPropsType) {
  const rawData = props.jsonObject as GenericTableRawData;
  // We don't want to parse Duration, because it is not a percentage!
  const formattedData = formatData<PortfolioMetricsMultipleBarChartsFormattedData>({ rawData });

  // Max abs value for %CLP and %UF to center the origin.
  const maxAbsRelativePosition = Math.max(
    Math.abs(formattedData.ACT['%CLP']),
    Math.abs(formattedData.ACT['%UF'])
  );

  // Calculate a specific domain for Duration in the relative position chart
  // This prevents the Duration bar from being too small to see when its
  // value is much lower than %CLP or %UF
  const absRelativeDuration = Math.abs(formattedData.ACT['Duración']);
  // Add a minimum domain size (0.2) and a 20% margin for better visibility
  const durationRelativeDomain: [number, number] = [
    -Math.max(0.2, absRelativeDuration * 1.2),
    Math.max(0.2, absRelativeDuration * 1.2),
  ];

  return (
    <div className="row">
      <div className="col-4 d-flex flex-column gap-3">
        <h5 className="fs-sm text-center fw-normal">Cartera</h5>
        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            showLabel
            config={{ y: { unit: '%' } }}
            data={[{ name: '% CLP', amount: formattedData.PORTF['%CLP'] }, { name: '% UF', amount: formattedData.PORTF['%UF'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
          />

        </ResponsiveContainer>
        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            showLabel
            config={{ y: { unit: '' } }}
            data={[{ name: 'Duración', amount: formattedData.PORTF['Duración'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
            yAxisDomain={[0, 'dataMax + 2']}
          />

        </ResponsiveContainer>
      </div>
      <div className="col-4 d-flex flex-column gap-3">
        <h5 className="fs-sm text-center fw-normal">Benchmark</h5>

        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            showLabel
            config={{ y: { unit: '%' } }}
            data={[{ name: '% CLP', amount: formattedData.BENCH['%CLP'] }, { name: '% UF', amount: formattedData.BENCH['%UF'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
          />

        </ResponsiveContainer>
        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            showLabel
            config={{ y: { unit: '' } }}
            data={[{ name: 'Duración', amount: formattedData.BENCH['Duración'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
            yAxisDomain={[0, 'dataMax + 2']}
          />

        </ResponsiveContainer>
      </div>
      <div className="col-4 d-flex flex-column gap-3">
        <h5 className="fs-sm text-center fw-normal">Pos. relativa</h5>

        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            overrideColorsByValueSign
            showLabel
            config={{ y: { unit: '%' } }}
            data={[{ name: '% CLP', amount: formattedData.ACT['%CLP'] }, { name: '% UF', amount: formattedData.ACT['%UF'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
            originReferenceLine
            // Use a symmetric domain centered at zero for visual balance
            yAxisDomain={[-maxAbsRelativePosition, maxAbsRelativePosition]}
          />

        </ResponsiveContainer>

        <ResponsiveContainer height={175} width="100%">
          <BaseBarChart<ChartData>
            overrideColorsByValueSign
            showLabel
            config={{ y: { unit: '' } }}
            data={[{ name: 'Duración', amount: formattedData.ACT['Duración'] }]}
            margin={SHARED_MARGINS}
            layout="vertical"
            mainAxisDataKey="name"
            dataKey="amount"
            legendLabels={{}}
            originReferenceLine
            // Use a custom domain for Duration so the bar is always visible
            yAxisDomain={durationRelativeDomain}
          />

        </ResponsiveContainer>
      </div>

    </div>
  );
}
